// AdCard.tsx
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Trash2, Bookmark, Lock, LockKeyhole, Link as LinkIcon, MoreVertical, FileEdit, Sparkles, Download, Check } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, memo } from "react";
import GradientHeart from "./GradientHeart";
import { BookmarkMenu } from "./boards/BookmarkMenu";
import { EntitlementMask } from "./EntitlementMask";
import { toast } from "@/hooks/use-toast";
// Removed framer-motion for memory optimization - using CSS transitions instead

// NEW: utility to join classes locally if you prefer
const cx = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(" ");
const LIBRARY_STATE_KEY = "obyect_library_state_v2";
const DEFAULT_STATE_KEY = "obyect_grid_state_v1";



export interface Ad {
  id: string;
  brand: string;
  category: string;
  start: string;
  end: string;
  copy: string;
  image: string;
  imagePath?: string;
  ctr: number;
  conversions: number;
  engagement: number;
  brandLogoUrl?: string;
  intrinsicWidth?: number;
  intrinsicHeight?: number;
  /** Whether the ad is currently active on Facebook (green dot if true, red if false/null) */
  is_active_on_facebook?: boolean | null;
  /** Number of days the ad has been running */
  days_running?: number | null;
}

interface AdCardProps {
  ad: Ad;
  className?: string;
  showDelete?: boolean;
  onDelete?: (ad: Ad) => void;
  showBlur?: boolean;
  onUpgradeClick?: () => void;
  eager?: boolean;
  isAdmin?: boolean;
  masked?: boolean;
  stateStorageKey?: string; // fixed position return key
  /** NEW: when set, media renders in a fixed aspect box (e.g. '4/5', '1/1', '16/9') */
  uniformAspect?: string; // like "4/5"
}

function MediaWithPortraitPreload({
  src,
  alt,
  eager,
  loadedBlur,
  onError,
}: {
  src: string;
  alt: string;
  eager?: boolean;
  loadedBlur?: boolean;
  onError?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [padTop, setPadTop] = useState<string>("125%"); // 4:5 portrait placeholder

  return (
    <div className="relative">
      {/* reserve height in normal flow so masonry spacing is correct before load */}
      <div style={{ paddingTop: padTop }} />

      {/* skeleton */}
      <div
        className={cx(
          "absolute inset-0 transition-opacity duration-200",
          loaded ? "opacity-0 pointer-events-none" : "opacity-100"
        )}
      >
        <div className="absolute inset-0 rounded-[5px] bg-neutral-200 dark:bg-neutral-800" />
        <div className="absolute inset-0 rounded-[5px] ring-1 ring-black/10 dark:ring-white/10" />
        <div className="absolute inset-0 rounded-[5px] shadow-[0_4px_16px_rgba(0,0,0,0.05)]" />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/10 to-transparent rounded-b-[5px]" />
      </div>

      {/* NOTE:
         - On small screens we use object-contain so users see the whole ad.
         - On larger screens we keep object-cover.
      */}
      <img
        src={src}
        alt={alt}
        className={cx(
          "absolute inset-0 w-full h-full transition-opacity duration-300 ease-out",
          loaded ? "opacity-100" : "opacity-0",
          loadedBlur && "blur-md",
          "object-contain sm:object-cover" // <-- key line
        )}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : "auto"}
        decoding="async"
        draggable={false}
        onLoad={(e) => {
          const img = e.currentTarget;
          const w = img.naturalWidth || 1;
          const h = img.naturalHeight || 1;
          setPadTop(`${(h / w) * 100}%`);
          setLoaded(true);
        }}
        onError={() => {
          onError?.();
          setLoaded(false);
        }}
      />
    </div>
  );
}

const AdCard = ({
  ad,
  className,
  showDelete,
  onDelete,
  showBlur,
  onUpgradeClick,
  eager = false,
  isAdmin = false,
  masked = false,
  uniformAspect,
  stateStorageKey,
}: AdCardProps) => {
  const brandInitial = ad.brand?.charAt(0).toUpperCase() ?? "A";
  const { isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  // determine if we should show the mask
  const isMasked = !!(masked || showBlur);
  // reset preload state when image src changes
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rippleActive, setRippleActive] = useState(false);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setLoaded(false);
    setImageError(false);
  }, [ad.image]);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleCardClick = () => {
    if (isMasked && onUpgradeClick) {
      onUpgradeClick();
      return;
    }
    if (isMasked) return;

    try {
      if (typeof window !== "undefined") {
        const key = stateStorageKey ?? DEFAULT_STATE_KEY;
        const raw = window.sessionStorage.getItem(key);
        const prev = raw ? JSON.parse(raw) : {};
        const next = {
          ...prev,
          lastAdId: ad.id,
          scrollY: window.scrollY,
        };
        window.sessionStorage.setItem(key, JSON.stringify(next));
      }
    } catch {
      // ignore
    }

    navigate(`/ads/${ad.id}`);
  };



  const handleCopyLink = (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    // Calculate ripple position relative to the clicked element
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setRipplePosition({ x, y });
    setRippleActive(true);

    // Reset ripple after animation completes
    setTimeout(() => setRippleActive(false), 500);

    const url = `${window.location.origin}/ads/${ad.id}`;
    setCopied(true);
    toast({
      title: "Link copied",
      description: "Ad link copied to clipboard.",
    });
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).catch(() => {
        // Fallback if clipboard API fails

      });
    } else {
      // Fallback for older browsers
      window.prompt("Copy this ad URL:", url);
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      const response = await fetch(ad.image);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Generate filename from brand and ad id
      const extension = ad.image.split('.').pop()?.split('?')[0] || 'jpg';
      link.download = `${ad.brand.replace(/\s+/g, '-').toLowerCase()}-${ad.id}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Downloaded",
        description: "Image saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download the image.",
        variant: "destructive",
      });
    }
  };

  const fav = isFavorite(String(ad.id));

  return (
    <div
      className={cn("masonry-item inline-block w-full mb-3", className)}
      data-ad-id={ad.id}
      id={`ad-${ad.id}`}
    >
      {/* === OUTER NON-SCALING FRAME === */}
      <div
        className="
          group relative rounded-[16px]
          transition-colors duration-300
          hover:z-[40]
        "
        style={{ position: "relative", zIndex: 0 }}
      >
        {/* === GLOW EFFECT (Absolute behind) === */}
        <div
          className="absolute -inset-[1px] rounded-[16px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm bg-gradient-to-b from-primary/20 to-transparent"
          aria-hidden="true"
        />

        {/* === INNER CONTENT === */}
        <div
          role="button"
          tabIndex={0}
          onClick={handleCardClick}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleCardClick()}
          className={cx(
            "relative z-10 rounded-[16px] focus:outline-none",
            "transition-transform duration-200 ease-out",
            !isMasked && "group-hover:-translate-y-1 group-active:scale-[0.99]",
            isMasked && "cursor-not-allowed opacity-80"
          )}
        >
          <Card className="border-0 ring-1 ring-white/5 shadow-2xl bg-[#13171c] rounded-[16px] overflow-hidden inner-glow">
            {/* === HEADER (Brand + Category + Actions) === */}
            <div className={cx("p-4 flex items-center justify-between", isMasked && "opacity-70")}>
              <div className="flex items-center gap-3">
                {/* Avatar with Facebook status indicator */}
                <div className="relative">
                  <Avatar className="h-8 w-8 ring-1 ring-white/10">
                    {ad.brandLogoUrl ? (
                      <AvatarImage src={ad.brandLogoUrl} alt={`${ad.brand} logo`} />
                    ) : (
                      <AvatarFallback className="bg-white/5 text-white/80 text-xs">
                        {brandInitial}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  {/* Facebook active status indicator dot */}
                  <span
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#13171c]",
                      ad.is_active_on_facebook ? "bg-green-500" : "bg-red-500"
                    )}
                    title={ad.is_active_on_facebook ? "Active on Facebook" : "Inactive on Facebook"}
                    aria-label={ad.is_active_on_facebook ? "Active on Facebook" : "Inactive on Facebook"}
                  />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold leading-none text-white/90">{ad.brand}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-white/50">{ad.category}</span>
                    {ad.days_running && ad.days_running > 0 && (
                      <span className="text-xs text-white/40">
                        · {ad.days_running} {ad.days_running === 1 ? "day" : "days"}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions: Delete + Dropdown Menu */}
              <div
                className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {showDelete && onDelete && isAdmin && (
                  <Button
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(ad);
                    }}
                    aria-label="Delete ad"
                    className="p-1 h-8 w-8 hover:bg-white/10 focus-visible:ring-0 ml-1 text-red-400 hover:text-red-300 rounded-full"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                {/* Action Dropdown Menu - disabled for masked ads */}
                {isMasked ? (
                  // 🔒 Masked ads: dropdown is purely visual, completely non-clickable
                  <div
                    aria-hidden="true"
                    className="p-0 h-auto w-auto opacity-50 cursor-not-allowed pointer-events-none"
                  >
                    <MoreVertical className="h-4 w-4 text-white/40" />
                  </div>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        aria-label="More actions"
                        className="p-1 h-8 w-8 hover:bg-white/10 focus-visible:ring-0 rounded-full text-white/60 hover:text-white"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <BookmarkMenu
                        adId={String(ad.id)}
                        trigger={
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Bookmark className="h-4 w-4 mr-2" />
                            Save in collection
                          </DropdownMenuItem>
                        }
                      />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLink(e);
                        }}
                        className={cx(
                          "relative overflow-hidden transition-colors duration-200",
                          copied && "bg-green-500/20 animate-scale-bounce"
                        )}
                      >
                        {rippleActive && (
                          <span
                            className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
                            style={{
                              left: ripplePosition.x,
                              top: ripplePosition.y,
                              width: '16px',
                              height: '16px',
                            }}
                          />
                        )}
                        {copied ? (
                          <Check className="h-4 w-4 mr-2 text-green-500 animate-scale-in" />
                        ) : (
                          <LinkIcon className="h-4 w-4 mr-2" />
                        )}
                        {copied ? "Copied!" : "Copy link"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/briefings/new?ad_id=${ad.id}`);
                        }}
                      >
                        <FileEdit className="h-4 w-4 mr-2" />
                        Start briefing
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ads/${ad.id}`);
                        }}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        AI analysis
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownload}>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>


            </div>

            {/* === MEDIA (Fully Responsive, No Horizontal Overflow) === */}
            <div className="relative px-3 pb-3">
              {uniformAspect ? (
                <div className={cx("relative rounded-[8px] overflow-hidden", isMasked && "opacity-80")}>
                  <div
                    className="
                      relative w-full aspect-[--ad-aspect]
                      bg-black/20
                      overflow-hidden
                    "
                    style={{ ["--ad-aspect" as any]: uniformAspect }}
                  >
                    <img
                      src={ad.image}
                      alt={`${ad.brand} ad creative`}
                      className={cx(
                        "absolute inset-0 w-full h-full object-contain lg:object-cover transition",
                        isMasked && "blur-[2px] grayscale-[45%]"
                      )}
                      loading={eager ? "eager" : "lazy"}
                      fetchPriority={eager ? "high" : "auto"}
                      decoding="async"
                      draggable={false}
                      onError={() => setImageError(true)}
                    />
                    <div className="absolute inset-0 ring-1 ring-white/5 pointer-events-none" />
                    {isMasked && (
                      <div
                        className="absolute inset-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpgradeClick?.();
                        }}
                      >
                        <EntitlementMask />
                        {/* Centered lock icon */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black/60 text-white backdrop-blur-md">
                            <Lock className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={cx("relative rounded-[8px] overflow-hidden", isMasked && "opacity-80")}>
                  <MediaWithPortraitPreload
                    src={ad.image}
                    alt={`${ad.brand} ad creative`}
                    eager={eager}
                    loadedBlur={isMasked || showBlur}
                    onError={() => setImageError(true)}
                  />
                  {isMasked && (
                    <>
                      {/* extra filter layer because the image component controls its own blur on-load */}
                      <div className="pointer-events-none absolute inset-0 backdrop-blur-[1.5px]" />
                      <div
                        className="absolute inset-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpgradeClick?.();
                        }}
                      >
                        <EntitlementMask />
                        {/* Centered lock icon */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-black/30 backdrop-blur-md">
                            <LockKeyhole className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
// This is especially important when new items are added to the grid -
// existing cards should not re-render unless their specific data changes
const arePropsEqual = (prevProps: AdCardProps, nextProps: AdCardProps) => {
  // Compare primitive props
  if (
    prevProps.className !== nextProps.className ||
    prevProps.showDelete !== nextProps.showDelete ||
    prevProps.showBlur !== nextProps.showBlur ||
    prevProps.eager !== nextProps.eager ||
    prevProps.isAdmin !== nextProps.isAdmin ||
    prevProps.masked !== nextProps.masked ||
    prevProps.uniformAspect !== nextProps.uniformAspect ||
    prevProps.stateStorageKey !== nextProps.stateStorageKey
  ) {
    return false;
  }

  // Compare ad object - use ad.id as primary key
  // If the id is the same, check other properties that affect rendering
  if (prevProps.ad.id !== nextProps.ad.id) {
    return false;
  }

  // Deep compare critical ad properties that might change and affect rendering
  if (
    prevProps.ad.brand !== nextProps.ad.brand ||
    prevProps.ad.category !== nextProps.ad.category ||
    prevProps.ad.image !== nextProps.ad.image ||
    prevProps.ad.brandLogoUrl !== nextProps.ad.brandLogoUrl ||
    prevProps.ad.is_active_on_facebook !== nextProps.ad.is_active_on_facebook ||
    prevProps.ad.days_running !== nextProps.ad.days_running
  ) {
    return false;
  }

  // Compare function props by reference
  // These are typically stable if wrapped in useCallback
  if (
    prevProps.onDelete !== nextProps.onDelete ||
    prevProps.onUpgradeClick !== nextProps.onUpgradeClick
  ) {
    return false;
  }

  // Props are equal - skip re-render
  return true;
};

export default memo(AdCard, arePropsEqual);
